import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  return src;
};

const CATS = [
  { id: "all", label: "All" },
  { id: "fresh", label: "Fresh" },
  { id: "smoked", label: "Smoked" },
  { id: "dried", label: "Dried" },
  { id: "frozen", label: "Frozen" },
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/products", { params: { category: cat } }).then((r) => setProducts(r.data));
  }, [cat]);

  return (
    <div data-testid="products-page">
      <SEO
        title="Fish & Seafood Products"
        description="Browse our full catalogue of fresh, smoked, dried and frozen seafood. Premium quality, sustainably sourced, delivered across the UK."
        path="/products"
      />
      <section className="bg-[color:var(--color-primary)] text-white py-20">
        <div className="container-x px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-4">Our Catch</div>
          <h1 className="font-display text-5xl md:text-6xl">Fish Products</h1>
          <p className="text-white/70 max-w-2xl mt-4">Browse our daily selection of sustainably-sourced fresh, smoked, dried, and frozen seafood.</p>
        </div>
      </section>

      <section className="section bg-[color:var(--color-bg)]">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="product-filters">
            {CATS.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest font-medium transition ${
                  cat === c.id
                    ? "bg-[color:var(--color-primary)] text-white"
                    : "bg-white border border-[color:var(--color-border)] text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {products.length === 0 ? (
            <div className="text-center py-24 text-[color:var(--color-text-secondary)]" data-testid="no-products">
              No products in this category yet. Check back soon!
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className="product-card block bg-white border border-[color:var(--color-border)] rounded-lg overflow-hidden"
                  data-testid={`product-card-${p.id}`}
                >
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
                    <img src={resolveImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
                    {p.in_stock && (
                      <span className="absolute top-3 left-3 bg-emerald-600 text-white text-xs uppercase tracking-widest px-2 py-1 rounded">In Stock</span>
                    )}
                    {!p.in_stock && (
                      <span className="absolute top-3 left-3 bg-gray-600 text-white text-xs uppercase tracking-widest px-2 py-1 rounded">Out of Stock</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl text-[color:var(--color-primary)] mb-1">{p.name}</h3>
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">{p.weight} · {p.category}</div>
                    <p className="text-sm text-[color:var(--color-text-secondary)] line-clamp-2 mb-4">{p.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="font-display text-2xl text-[color:var(--color-accent)]">
                        {p.variants && p.variants.length > 0
                          ? `from £${Math.min(...p.variants.map(v => Number(v.price))).toFixed(2)}`
                          : `£${p.price?.toFixed(2)}`}
                      </div>
                      <span className="text-xs uppercase tracking-widest text-[color:var(--color-primary)]">View →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
